<template>
    <div v-if="hasAnyAccessories">
        <h3>{{ translations.heading }}</h3>

        <!-- Cables -->
        <div v-if="accessories.cables && accessories.cables.length > 0" class="selection_container">
            <h3>{{ translations.cables }}</h3>
            <div class="select_wrapper">
                <select v-model="selectedCable">
                    <option value="">{{ translations.placeholder }}</option>
                    <option
                        v-for="cable in accessories.cables"
                        :key="cable.id"
                        :value="cable.id"
                    >
                        {{ cable.title }}
                    </option>
                </select>
            </div>
        </div>

        <!-- Power Supplies -->
        <div v-if="accessories.powerSupplies && accessories.powerSupplies.length > 0" class="selection_container">
            <h3>{{ translations.powerSupplies }}</h3>
            <div class="select_wrapper">
                <select v-model="selectedPowerSupply">
                    <option value="">{{ translations.placeholder }}</option>
                    <option
                        v-for="ps in accessories.powerSupplies"
                        :key="ps.id"
                        :value="ps.id"
                    >
                        {{ ps.title }}
                    </option>
                </select>
            </div>
        </div>

        <!-- Lenses -->
        <div v-if="accessories.lenses && accessories.lenses.length > 0" class="selection_container">
            <h3>{{ translations.lenses }}</h3>
            <div class="select_wrapper">
                <select v-model="selectedLens">
                    <option value="">{{ translations.placeholder }}</option>
                    <option
                        v-for="lens in accessories.lenses"
                        :key="lens.id"
                        :value="lens.id"
                    >
                        {{ lens.title }}
                    </option>
                </select>
            </div>
        </div>

        <!-- Housing -->
        <div v-if="accessories.housing && accessories.housing.length > 0" class="selection_container">
            <h3>{{ translations.housing }}</h3>
            <div class="select_wrapper">
                <select v-model="selectedHousing">
                    <option value="">{{ translations.placeholder }}</option>
                    <option
                        v-for="housing in accessories.housing"
                        :key="housing.id"
                        :value="housing.id"
                    >
                        {{ housing.title }}
                    </option>
                </select>
            </div>
        </div>

        <!-- Mounting & Fixation -->
        <div v-if="accessories.mountingFixation && accessories.mountingFixation.length > 0" class="selection_container">
            <h3>{{ translations.mountingFixation }}</h3>
            <div class="select_wrapper">
                <select v-model="selectedMountingFixation">
                    <option value="">{{ translations.placeholder }}</option>
                    <option
                        v-for="mf in accessories.mountingFixation"
                        :key="mf.id"
                        :value="mf.id"
                    >
                        {{ mf.title }}
                    </option>
                </select>
            </div>
        </div>

        <!-- Cooling -->
        <div v-if="accessories.cooling && accessories.cooling.length > 0" class="selection_container">
            <h3>{{ translations.cooling }}</h3>
            <div class="select_wrapper">
                <select v-model="selectedCooling">
                    <option value="">{{ translations.placeholder }}</option>
                    <option
                        v-for="cooling in accessories.cooling"
                        :key="cooling.id"
                        :value="cooling.id"
                    >
                        {{ cooling.title }}
                    </option>
                </select>
            </div>
        </div>

        <!-- Boards -->
        <div v-if="accessories.boards && accessories.boards.length > 0" class="selection_container">
            <h3>{{ translations.boards }}</h3>
            <div class="select_wrapper">
                <select v-model="selectedBoard">
                    <option value="">{{ translations.placeholder }}</option>
                    <option
                        v-for="board in accessories.boards"
                        :key="board.id"
                        :value="board.id"
                    >
                        {{ board.title }}
                    </option>
                </select>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'CameraAccessoriesSelection',

    props: {
        accessories: {
            type: Object,
            default: () => ({ cables: [], powerSupplies: [], lenses: [], housing: [], mountingFixation: [], cooling: [], boards: [] }),
        },
        translations: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            selectedCable: '',
            selectedPowerSupply: '',
            selectedLens: '',
            selectedHousing: '',
            selectedMountingFixation: '',
            selectedCooling: '',
            selectedBoard: '',
        };
    },

    computed: {
        hasAnyAccessories() {
            return (
                (this.accessories.cables && this.accessories.cables.length > 0) ||
                (this.accessories.powerSupplies && this.accessories.powerSupplies.length > 0) ||
                (this.accessories.lenses && this.accessories.lenses.length > 0) ||
                (this.accessories.housing && this.accessories.housing.length > 0) ||
                (this.accessories.mountingFixation && this.accessories.mountingFixation.length > 0) ||
                (this.accessories.cooling && this.accessories.cooling.length > 0) ||
                (this.accessories.boards && this.accessories.boards.length > 0)
            );
        },
    },

    mounted() {
        this.syncToGlobal();
    },

    watch: {
        selectedCable() {
            this.syncToGlobal();
        },
        selectedPowerSupply() {
            this.syncToGlobal();
        },
        selectedLens() {
            this.syncToGlobal();
        },
        selectedHousing() {
            this.syncToGlobal();
        },
        selectedMountingFixation() {
            this.syncToGlobal();
        },
        selectedCooling() {
            this.syncToGlobal();
        },
        selectedBoard() {
            this.syncToGlobal();
        },
    },

    methods: {
        syncToGlobal() {
            const selections = [];

            if (this.selectedCable) {
                selections.push({
                    accessoryType: 'Cable',
                    accessoryId: this.selectedCable,
                });
            }

            if (this.selectedPowerSupply) {
                selections.push({
                    accessoryType: 'PowerSupply',
                    accessoryId: this.selectedPowerSupply,
                });
            }

            if (this.selectedLens) {
                selections.push({
                    accessoryType: 'Lens',
                    accessoryId: this.selectedLens,
                });
            }

            if (this.selectedHousing) {
                selections.push({
                    accessoryType: 'Housing',
                    accessoryId: this.selectedHousing,
                });
            }

            if (this.selectedMountingFixation) {
                selections.push({
                    accessoryType: 'MountingFixation',
                    accessoryId: this.selectedMountingFixation,
                });
            }

            if (this.selectedCooling) {
                selections.push({
                    accessoryType: 'Cooling',
                    accessoryId: this.selectedCooling,
                });
            }

            if (this.selectedBoard) {
                selections.push({
                    accessoryType: 'Board',
                    accessoryId: this.selectedBoard,
                });
            }

            window.cameraAccessoriesSelections = selections;
        },
    },
};
</script>
